import { Injectable } from "@angular/core";
import {
  ActivatedRouteSnapshot,
  CanActivate,
  RouterStateSnapshot,
} from "@angular/router";
import { Router, ActivatedRoute } from "@angular/router";
import { StateService } from "./state.service";
import { DomainNavigationService } from "./domain-navigation.service";
import { CatExpertRouterLink } from "../app-routing-enum";
import { Observable, of } from "rxjs";
import { map, catchError } from "rxjs/operators";
@Injectable({
  providedIn: "root",
})
export class AuthGuard implements CanActivate {
  constructor(
    private router: Router,
    private stateService: StateService,
    private activatedRoute: ActivatedRoute,
    private domainNavigationService: DomainNavigationService,
  ) {}

  canActivate(): boolean {
    if (
      !window.localStorage.c ||
      !window.localStorage.i ||
      !window.localStorage.d
    ) {
      return true;
    }

    // Redirect to domain-specific default route
    this.domainNavigationService.navigateToDefaultRoute();
    return false;
  }
}
@Injectable({
  providedIn: "root",
})
export class VerifyAuthGuard implements CanActivate {
  constructor(private router: Router) {}
  canActivate(): boolean {
    if (
      window.localStorage.c ||
      window.localStorage.i ||
      window.localStorage.d
    ) {
      return true;
    }

    this.router.navigateByUrl("/login");
    return false;
  }
}

@Injectable({
  providedIn: "root",
})
export class VerifyPHODomain implements CanActivate {
  constructor(
    private router: Router,
    public stateService: StateService,
  ) {}

  canActivate(): boolean {
    if (this.stateService.checkAppConfig() === "pho") {
      return true;
    }

    this.router.navigateByUrl("/studio");
    return false;
  }
}

@Injectable({
  providedIn: "root",
})
export class VerifyCatexpertDomain implements CanActivate {
  constructor(
    private router: Router,
    public stateService: StateService,
  ) {}

  canActivate(): boolean {
    if (this.stateService.checkAppConfig() === "catexpert" || "space") {
      return true;
    }

    this.router.navigateByUrl("/studio");
    return false;
  }
}

@Injectable({
  providedIn: "root",
})
export class VerifySpaceDomain implements CanActivate {
  constructor(
    private router: Router,
    public stateService: StateService,
  ) {}

  canActivate(): boolean {
    if (
      this.stateService.checkAppConfig() === "space" ||
      this.stateService.checkAppConfig() === "catexpert"
    ) {
      return true;
    }

    this.router.navigateByUrl("/studio");
    return false;
  }
}

@Injectable({
  providedIn: "root",
})
export class VerifyProductLibrary implements CanActivate {
  constructor(
    private router: Router,
    public stateService: StateService,
  ) {}

  canActivate(): boolean {
    // Allow access for Admin, Zone Admin, or Library Admin roles
    if (
      this.stateService.hasRole("Admin") ||
      this.stateService.hasZoneAdminAndScope() ||
      this.stateService.hasRole("Library Admin")
    ) {
      return true;
    }

    this.router.navigateByUrl("/studio");
    return false;
  }
}

@Injectable({
  providedIn: "root",
})
export class VerifyAiImagery implements CanActivate {
  constructor(
    private router: Router,
    public stateService: StateService,
  ) {}

  canActivate(): Observable<boolean> {
    // Check if we already have valid user session data
    const existingUser = this.stateService.getUser();

    if (existingUser && existingUser.id && existingUser.roles) {
      // We have valid session data, check roles directly
      const hasAccess = this.stateService.hasAiImageryAccessRole();
      const hasAdmin = this.stateService.hasAiImageryLibraryAdminRole();

      if (hasAccess || hasAdmin) {
        return of(true);
      }

      this.router.navigateByUrl("/unauthorized/ai-imagery-access");
      return of(false);
    }

    // No valid session data, call the user/me API
    return this.stateService.getUserDetailsAPICall().pipe(
      map((userResponse: any) => {
        // Update user details in state service
        // Handle different possible response structures
        let userData = null;
        if (userResponse) {
          // Check if response has data property
          if (userResponse.data) {
            userData = userResponse.data;
          }
          // Check if response itself is the user data
          else if (
            userResponse.id ||
            userResponse.email ||
            userResponse.roles
          ) {
            userData = userResponse;
          }
        }

        if (userData) {
          this.stateService.setUser(userData);
          localStorage.setItem("user-name", JSON.stringify(userData));
        }

        // Check roles after updating user data
        const hasAccess = this.stateService.hasAiImageryAccessRole();
        const hasAdmin = this.stateService.hasAiImageryLibraryAdminRole();

        // Check if user has either Ai Imagery Access Role or Ai Imagery Library Admin Role
        if (hasAccess || hasAdmin) {
          return true;
        }

        this.router.navigateByUrl("/unauthorized/ai-imagery-access");
        return false;
      }),
      catchError((error) => {
        // On API error, fallback to checking existing user data
        const hasAccess = this.stateService.hasAiImageryAccessRole();
        const hasAdmin = this.stateService.hasAiImageryLibraryAdminRole();

        if (hasAccess || hasAdmin) {
          return of(true);
        }

        this.router.navigateByUrl("/unauthorized/ai-imagery-access");
        return of(false);
      }),
    );
  }
}

@Injectable({
  providedIn: "root",
})
export class DomainRouteGuard implements CanActivate {
  constructor(
    private router: Router,
    private stateService: StateService,
  ) {}

  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot,
  ): boolean {
    const domainType = this.stateService.checkAppConfig();
    const currentPath = state.url.split("/")[1];

    // If accessing home root and domain is ai-imagery, redirect to ai-imagery login screen
    if (currentPath === "home" && domainType === "ai-imagery") {
      this.router.navigateByUrl(`/${CatExpertRouterLink.Login}`);
      return false;
    }

    // If accessing studio root and domain is ai-imagery, redirect to ai-imagery section
    if (currentPath === "studio" && domainType === "ai-imagery") {
      this.router.navigateByUrl(`/${CatExpertRouterLink.AiImagery}`);
      return false;
    }

    // If accessing ai-imagery root and domain is not ai-imagery, redirect to studio
    if (currentPath === "/ai-imagery" && domainType !== "ai-imagery") {
      this.router.navigateByUrl(`/${CatExpertRouterLink.Studio}`);
      return false;
    }

    return true;
  }
}

@Injectable({
  providedIn: "root",
})
export class VerifyLibraries implements CanActivate {
  constructor(
    private router: Router,
    public stateService: StateService,
  ) {}

  canActivate(): boolean {
    // Allow access for Admin, or Library Admin roles
    if (
      this.stateService.hasAdminRole() ||
      this.stateService.hasZoneAdminRole() ||
      this.stateService.hasLibraryAdminRole()
    ) {
      return true;
    }

    this.router.navigateByUrl(
      `${CatExpertRouterLink.Studio}/${CatExpertRouterLink.Libraries}`,
    );
    return false;
  }
}

@Injectable({
  providedIn: "root",
})
export class BulkPlanoRouteGuard implements CanActivate {
  constructor(
    private router: Router,
    public stateService: StateService,
    private route: ActivatedRoute,
  ) {}

  canActivate(route: ActivatedRouteSnapshot): Observable<boolean> {
    const urlExpid = route.parent?.paramMap.get("id");

    // If we have experimentId in either history state or session storage, use it to fetch golden rules
    if (urlExpid) {
      sessionStorage.setItem("expId", urlExpid);
    }
    const expId = sessionStorage.getItem("expId");
    const studioRoute = `/${CatExpertRouterLink.Studio}`;
    if (!expId) {
      this.router.navigateByUrl(studioRoute);
      return of(false);
    }

    return this.stateService.getSidebarStatesByExpId().pipe(
      map((response: any) => {
        if (response && response.states) {
          const states = response.states;
          const navigationPriority = [
            {
              title: "planograms-initialization",
              slug: CatExpertRouterLink.PlanogramSummary,
            },
            {
              title: "rules-definition",
              slug: CatExpertRouterLink.GoldenRules,
            },
            {
              title: "products-selection",
              slug: CatExpertRouterLink.ProductLibrary,
            },
            {
              title: "fixtures-selection",
              slug: CatExpertRouterLink.FixtureLibrary,
            },
            {
              title: "scope-definition",
              slug: CatExpertRouterLink.DefineScope,
            },
          ];
          const lastCompletedStep = navigationPriority.find((step) =>
            states.find(
              (s: any) => s.title === step.title && s.status === "COMPLETED",
            ),
          );
          const isPlanoSummaryPending = states.find(
            (s: any) =>
              s.title === "planograms-initialization" &&
              s.status !== "COMPLETED",
          );
          if (!lastCompletedStep) {
            this.router.navigate([
              `${studioRoute}/${CatExpertRouterLink.BulkPlanogram}/${CatExpertRouterLink.Experiments}/${expId}/${CatExpertRouterLink.BulkPlanogramScope}`,
            ]);
            return false;
          }
          // If Planogram Summary is pending, navigate to Planogram Summary, else navigate to last completed step, else navigate to studio home
          if (this.router.url !== studioRoute && isPlanoSummaryPending) {
            return true;
          } else if (
            lastCompletedStep?.slug === CatExpertRouterLink.PlanogramSummary
            // Scenario 2: golden rules are saved — go straight to planogram-summary
            // so the user sees the summary page (with Edit Rules available) instead
            // of being dropped back onto the golden-rules editor.
          ) {
            return true;
          } else if (lastCompletedStep?.slug) {
            this.router.navigate([
              `${studioRoute}/${CatExpertRouterLink.BulkPlanogram}/${CatExpertRouterLink.Experiments}/${expId}/${lastCompletedStep.slug}`,
            ]);
            return false;
          } else {
            this.router.navigateByUrl(studioRoute);
            return false;
          }
        } else {
          this.router.navigateByUrl(studioRoute);
          return false;
        }
      }),
      catchError(() => {
        this.router.navigateByUrl(studioRoute);
        return of(false);
      }),
    );
  }
}
